import React, { memo, useMemo, useRef } from 'react';
import T from 'prop-types';

import Chart from './Chart';
import helpers from '../../utils/helpers';

let redrawOnMouseOut = true;
const defaultColor = '#F6F7FA';
const getRgbColors = colors => colors.map(color => helpers.hexToRGB(color, 0.4));

const initialConfig = ({
  colors,
  series,
  titleOptions,
  onPointClick,
  onPointOver,
  onPointOut,
}) => {
  const rgbColors = getRgbColors(colors);

  return ({
    chart: {
      type: 'pie',
      height: 200,
      plotBackgroundColor: null,
      plotBorderWidth: 0,
      plotShadow: false,
      backgroundColor: 'transparent',
    },
    colors,
    plotOptions: {
      pie: {
        borderWidth: 0,
        dataLabels: {
          enabled: false,
        },
        shadow: false,
        slicedOffset: 0,
      },
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click(event) {
              redrawOnMouseOut = false;
              if (onPointClick) {
                onPointClick(event);
              }
            },
            mouseOver: ({ target }) => {
              // triggers only on not selected points
              if (!target.selected) {
                redrawOnMouseOut = true;

                // Update color for inner ring series
                target.series.chart.series[1].points[target.index].update({
                  color: rgbColors[target.index],
                }, true, false, false); // [redraw, oneToOne, animation]

                if (onPointOver) {
                  onPointOver(target);
                }
              }
            },
            mouseOut: ({ target }) => {
              // triggers only on not selected points
              if (!target.selected) {
                // Update color for inner ring series
                target.series.chart.series[1].points[target.index].update({
                  color: defaultColor,
                }, redrawOnMouseOut); // [redraw]

                if (onPointOut) {
                  onPointOut(target);
                }
              }
            },
          },
        },
      },
    },
    series,
    title: {
      text: '',
      align: 'center',
      verticalAlign: 'middle',
      useHtml: true,
      y: 0,
      ...titleOptions,
    },
    tooltip: {
      enabled: true,
      backgroundColor: '#3b4255',
      borderRadius: 8,
      borderWidth: 0,
      hideDelay: 200,
      style: {
        opacity: 0.9,
        color: 'white',
        fontSize: '12px',
      },
      shadow: false,
      followPointer: false,
      useHTML: true,
      formatter() {
        return `<span>${this.key ? this.key : this.points[0].key} (${this.y})</span>`;
      },
    },
  });
};

const TwoRingDonutChart = ({
  onPointClick,
  onPointOver,
  onPointOut,
  series,
  titleOptions,
}) => {
  const donutChartRef = useRef();

  const colors = useMemo(() => (series || []).map(({ color }) => color), [series]);
  const rgbColors = getRgbColors(colors);

  const chartSeries = useMemo(() => {
    const data = series.map(({ name, value, selected }, index) => {
      const pointColor = selected ? rgbColors[index] : defaultColor;
      return [name, value, selected, pointColor];
    });

    return [{
      data,
      keys: ['name', 'y', 'selected'],
      innerSize: 132,
      size: 190,
    }, {
      data,
      keys: ['name', 'y', 'selected', 'color'],
      size: 132,
      innerSize: 98,
    }];
  }, [series]);

  const chartConfig = useMemo(() => initialConfig({
    colors,
    series: chartSeries,
    titleOptions,
    onPointClick,
    onPointOver,
    onPointOut,
  }), [colors, chartSeries, titleOptions]);

  return (
    <Chart
      ref={donutChartRef}
      config={chartConfig}
      chartId="two-ring-donut-chart"
      className="two-ring-donut-chart show-overflow"
    />
  );
};

TwoRingDonutChart.propTypes = {
  onPointClick: T.func,
  onPointOut: T.func,
  onPointOver: T.func,
  series: T.arrayOf(
    T.shape({
      name: T.string,
      color: T.string,
      value: T.number,
      selected: T.bool,
    }),
  ).isRequired,
  titleOptions: T.object,
};

TwoRingDonutChart.defaultProps = {
  titleOptions: {},
};

export default memo(TwoRingDonutChart);
